import { MessageAttachment, MessageRecord, MessageRecords } from "./types";
import { Box, Heading, Stack, Text, TextLink } from "@origin-digital/ods-core";
import { IconNewWindow } from "@origin-digital/ods-icons";
import { MessageDirectionEnum } from "../../Premises/types";
import { getCustomerAvatar, OriginUserAvatar } from "./Icons";
import { formatMessageDateTime } from "./helpers";
import React, { useContext, useEffect, useRef } from "react";
import { Paper, Table, TableContainer } from "@mui/material";
import { MessageContext } from "./MessageContext";

const MessageRecordAttachment = ({
  messageAttachment,
}: {
  messageAttachment: MessageAttachment;
}) => {
  const { fileUploadType, fileName, link } = messageAttachment;
  return (
    <Stack space="xxsmall">
      <Text>
        <TextLink href={link ? link : ""}>
          {fileName}
          <Box display="inline" paddingLeft="xxsmall">
            <IconNewWindow color="inherit" size="inherit" />
          </Box>
        </TextLink>
      </Text>
      <Text variant="body-small" tone="neutralLight">
        {fileUploadType}
      </Text>
    </Stack>
  );
};

const MessageRecordContainer = ({
  messageRecord,
}: {
  messageRecord: MessageRecord;
}) => {
  const { text, authorName, created, attachmentsMeta, direction } =
    messageRecord;
  return (
    <Box paddingY="xlarge">
      <Stack space="large">
        {/* Metadata */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          paddingX="xsmall"
        >
          <Box display="flex" alignItems="center">
            {direction === MessageDirectionEnum.INBOUND ? (
              getCustomerAvatar(authorName)
            ) : (
              <OriginUserAvatar />
            )}
            <Text variant="body-small" tone="neutralLight">
              {authorName}
            </Text>
          </Box>
          <Box marginLeft="xlarge">
            <Text variant="body-small" tone="neutralLight">
              {formatMessageDateTime(created)}
            </Text>
          </Box>
        </Box>
        {/* Metadata */}
        {/* Message Record*/}
        <Box
          id={"record-box"}
          marginX="xsmall"
          paddingX="large"
          paddingY="large"
          borderRadius="small"
          border="1px solid #E3E3E3"
          backgroundColor="grey50"
        >
          <Stack space="xlarge">
            {/* Message Record body */}
            <Stack space="medium">
              <div style={{ whiteSpace: "pre-wrap" }}>
                <Text>{text}</Text>
              </div>
            </Stack>
            {/* Message Record body */}
            {/* Message Record Attached Files */}
            {attachmentsMeta && attachmentsMeta.length > 0 && (
              <Stack space="medium">
                {/*@ts-expect-error: it works but throws error in ts*/}
                <Text variant="body-small" tone="medium">
                  Attachments:
                </Text>
                {attachmentsMeta?.map((messageAttachment) => (
                  <MessageRecordAttachment
                    key={messageAttachment.id}
                    messageAttachment={messageAttachment}
                  />
                ))}
              </Stack>
            )}
            {/* End Message Record Attached Files */}
          </Stack>
        </Box>
        {/* Message Record*/}
      </Stack>
    </Box>
  );
};

const MessageRecordTable = ({
  messageRecords,
}: {
  messageRecords: MessageRecords;
}) => {
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const { messageTabState, setMessageTabState } = useContext(MessageContext);
  const { processingMessagesFetched, processingMessageTabClicked } =
    messageTabState;

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
    setMessageTabState((prevMessageTabState) => ({
      ...prevMessageTabState,
      processingMessagesFetched: false,
    }));
  };

  useEffect(() => {
    if (processingMessagesFetched || processingMessageTabClicked) {
      scrollToBottom();
    }
  }, [messageRecords, processingMessagesFetched, processingMessageTabClicked]);

  return (
    <TableContainer
      component={Paper}
      sx={{
        border: "1px solid #E5E5E5",
        borderRadius: "8px",
        maxHeight: 700,
        overflow: "auto",
      }}
      ref={messagesEndRef}
    >
      <Table>
        {messageRecords.map((record) => (
          <MessageRecordContainer key={record.id} messageRecord={record} />
        ))}
      </Table>
    </TableContainer>
  );
};

const MessageRecordList = () => {
  const { messageTabState } = useContext(MessageContext);
  const { messageRecords } = messageTabState;
  return messageRecords?.length > 0 ? (
    <MessageRecordTable messageRecords={messageRecords} />
  ) : (
    <NoMessageRecordsView />
  );
};

const NoMessageRecordsView = () => (
  <Box paddingTop="medium" paddingBottom="large">
    <Stack space={["large", "xlarge"]} alignX="center">
      <svg
        width="96"
        height="96"
        viewBox="0 0 96 96"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#clip0_160_493)">
          <path
            d="M95.9816 47.99C95.9816 74.4945 74.4945 95.9799 47.99 95.9799C21.4855 95.9799 0 74.4945 0 47.99C0 21.4855 21.4855 0 47.99 0C74.4945 0 95.9816 21.4855 95.9816 47.99Z"
            fill="#F8F8F8"
          />
          <path
            d="M41.625 24V57H72.7729V39.1139C72.7729 30.7675 67.3843 24 60.7385 24H41.625Z"
            fill="#777777"
          />
          <path
            d="M48.819 57V74.01C48.819 81.525 54.1817 87.618 60.7959 87.618C67.4102 87.618 72.7729 81.525 72.7729 74.01V57H48.819Z"
            fill="#E1E1E1"
          />
          <path
            d="M35 94.1797L38.5 95L42 95.5703V53.417H35V94.1797Z"
            fill="#777777"
          />
          <path
            d="M48.7052 24H26.1705C19.4495 24 14 30.6104 14 38.7632V57H48.7052V38.7632C48.7052 30.6104 54.1547 24 60.8756 24H48.7052Z"
            fill="#B2B2B2"
          />
        </g>
        <defs>
          <clipPath id="clip0_160_493">
            <rect width="96" height="96" fill="white" />
          </clipPath>
        </defs>
      </svg>

      <Stack space={["medium", "large"]}>
        <Heading variant="h4" align="center">
          Nothing to see here!
        </Heading>
      </Stack>
    </Stack>
  </Box>
);

export default MessageRecordList;
